/**
 * KPI / Analytics 이벤트 트래커 — GA4 (Firebase Analytics) 연동.
 *
 * peeka 핵심 KPI:
 *   - MAU / 재방문율 / 체류 시간
 *   - **스캔 확정 성공률과 그 근거** (peeka 의 제품 품질 = 인식 정확도)
 *   - 비회원 5회 소진 → 로그인 전환율 (§4.1)
 *   - 스캔 확정 → 컬렉션 담기 전환율
 *   - 카탈로그 탐색(검색·세트·상세) 깊이
 *
 * 전송은 lib/analytics.ts (플랫폼 분기·큐·GA4 제한 처리) 가 담당하고,
 * 이 파일은 "무엇을 보낼 수 있는가" 의 계약(이벤트명 union + 파라미터 allow-list)을 소유한다.
 *
 * ⚠ PII 금지 (GA 약관 — 적발 시 계정 정지):
 *   이메일·이름·휴대폰·주소·자유입력 텍스트는 어떤 파라미터로도 보내지 않는다.
 *   이를 강제하기 위해 파라미터는 이벤트별 allow-list 에 등록된 키만 통과한다.
 *   새 파라미터가 필요하면 EVENT_PARAM_ALLOWLIST 에 키를 추가할 것 (등록되지 않은
 *   키는 조용히 제거되고 dev 에서 경고). 값에 PII 가 아닌지 확인 후 추가한다.
 *
 *   ⚠ **검색어 원문은 자유입력이라 보내지 않는다** — search_performed 는 길이와
 *   결과 수만 받는다. 카드명을 알고 싶으면 카탈로그 쪽 집계로 볼 것.
 *
 * ⚠ 고카디널리티 파라미터(cardSerialNo·cardsetSerialNo)는 **GA 커스텀 디멘션으로 등록하지
 *   말 것** — 등록하면 카디널리티 폭발로 리포트가 (other) 로 뭉개진다. 파라미터로만 두고
 *   개별 카드 분석이 필요하면 BigQuery export 에서 본다.
 *
 * 이벤트 taxonomy 전체 문서(왜 그렇게 정했는지·콘솔 등록 절차):
 *   duckflex-peeka-docs/output_docs/50_GUIDES/ANALYTICS_GA4.md
 */
import { env } from "../config/env";
import {
  logEvent,
  setAnalyticsUserId,
  setAnalyticsUserProperty,
  type AnalyticsParams,
} from "./analytics";

export type EventName =
  // --- Auth (소셜 전용 — 2026-08-04 재편으로 이메일·본인인증·카카오·네이버는 없다) ---
  | "auth_login_view"
  | "auth_oauth_start"
  | "auth_oauth_error"
  | "auth_login_success"
  | "auth_signup_success"
  | "terms_view"
  // --- Scan (peeka 핵심 루프) ---
  | "scan_started"
  | "scan_card_confirmed"
  | "scan_confirm_failed"
  | "scan_guest_limit_reached"
  | "scan_summary_view"
  | "scan_summary_saved"
  // --- 카탈로그 ---
  // ⚠ card_variant_selected(§3.3 세부 버전 선택)는 **의도적으로 없다** —
  //   VariantSelectPage 가 아직 더미 카탈로그(findCard)를 읽어 실 카드로 들어오면
  //   not-found 로 떨어진다. 실 API 로 옮길 때 이벤트도 같이 넣을 것.
  | "card_detail_view"
  | "card_share"
  | "search_performed"
  | "set_detail_view"
  // --- 컬렉션 ---
  | "collection_view"
  | "collection_holding_added"
  | "collection_holding_updated"
  | "collection_holding_removed";

/**
 * 스캔 확정 근거 — peeka 인식 품질의 핵심 축.
 * 어떤 신호가 카드를 확정시켰는지를 남긴다 (ScanPage confirmScan 의 수락 규칙과 1:1).
 *   match       : matcher 임베딩 확신만으로 확정
 *   number      : OCR 컬렉터 번호 유일 일치로 확정
 *   name_number : OCR 카드명 + 번호 동시 일치 (독립 신호 2개)
 *   ocr_found   : 카탈로그 확장검색이 OCR 번호로 찾아낸 카드
 *   manual      : 사용자 수동 셔터 (게이트 밖)
 */
export type ScanEvidence = "match" | "number" | "name_number" | "ocr_found" | "manual";

/**
 * 스캔 확정 실패 사유 — 사용자가 고칠 수 있는 것(노출/블러)과 우리가 고쳐야 하는 것
 * (임계 미달)을 가른다.
 *   below_threshold : 매칭·번호·이름 전부 부족
 *   exposure_held   : 어두움×약확신×무증거 게이트 (2026-08-27 역광 오적재 차단)
 *   blur_held       : OCR 0줄 + 약확신 보류
 *   no_detection    : 스틸에서 점착 타깃 카드를 못 찾음 (엔진 EMPTY)
 */
export type ScanFailReason =
  | "below_threshold"
  | "exposure_held"
  | "blur_held"
  | "no_detection";

export type EventProperties = Record<string, string | number | boolean | null>;

/**
 * 이벤트별 허용 파라미터 키 — 여기 없는 키는 전송 전 제거된다 (PII 유입 기본 차단).
 * 파라미터가 필요 없는 이벤트는 생략 (= 모든 파라미터 차단).
 *
 * memberSerialNo: 내부 회원 일련번호(가명 식별자, PII 아님 — 실명/연락처와 GA 안에서 결합 불가).
 * gift_token: 선물 공유 토큰(불투명 난수) — 웹 수신 ↔ 앱 가입 전환 퍼널 연결용.
 */
const EVENT_PARAM_ALLOWLIST: Partial<Record<EventName, readonly string[]>> = {
  // --- Auth ---
  auth_oauth_start: ["provider"],
  auth_oauth_error: ["provider", "reason"],
  // provider 를 넣어 둔다 — google/apple 별 가입·로그인 비중이 CS 대응의 근거다
  // (provider 별 별도 계정 정책이라 "지난번 수단"을 잊은 문의가 올라온다).
  auth_login_success: ["provider", "memberSerialNo"],
  auth_signup_success: ["provider", "memberSerialNo"],
  terms_view: ["type", "lang"],

  // --- Scan ---
  // 스캔 세션 진입. mode=scan|labeling (라벨링은 비운영 빌드 전용이라 사실상 scan).
  scan_started: ["mode", "scanLang"],
  /**
   * 카드 1장 확정 = peeka 의 핵심 전환. 파라미터는 전부 "왜 확정됐나" 를 설명한다.
   *   evidence   : ScanEvidence — 어떤 신호가 확정시켰나
   *   similarity : matcher top-1 유사도 (0~1, 소수 2자리) — GA4 가 평균을 낼 수 있게 수치로
   *   scanLang   : 사용자가 고른 인덱스 언어 / cardLang : 확정된 카드의 언어
   *                (둘이 다르면 언어팩 밖 실물을 비슷한 카드로 잡은 신호 — 2026-08-25 결정대로
   *                 앱이 막지 않으므로 **집계로만** 드러난다)
   *   manual     : 수동 셔터 여부 / guest : 비회원 여부(5회 한도 퍼널)
   */
  scan_card_confirmed: [
    "evidence",
    "similarity",
    "scanLang",
    "cardLang",
    "manual",
    "guest",
    "cardSerialNo",
  ],
  scan_confirm_failed: ["reason", "similarity", "manual", "scanLang"],
  // 비회원 5회 소진 — 로그인 오버레이가 뜨는 순간. 로그인 전환율의 분모.
  scan_guest_limit_reached: ["scanLang"],
  scan_summary_view: ["cardCount"],
  // 요약에서 일괄 담기. savedCount < cardCount 면 일부 실패.
  scan_summary_saved: ["cardCount", "savedCount"],

  // --- 카탈로그 ---
  // source: scan|search|home|set|collection — 카드 상세로 들어오는 경로 비중.
  card_detail_view: ["cardSerialNo", "source"],
  card_share: ["cardSerialNo", "channel"],
  // ⚠ 검색어 원문 금지 — 길이와 결과 수만. resultCount=0 이 카탈로그 구멍의 지표다.
  search_performed: ["queryLength", "resultCount", "language"],
  set_detail_view: ["cardsetSerialNo", "language"],

  // --- 컬렉션 ---
  collection_view: ["tab"],
  // source: scan_summary|scan_result|catalog — 담기가 어디서 일어나는지.
  // hasPurchasePrice: 구매가를 아는 카드 비중 (손익 지표의 모수 — §6.2 로 NULL 허용이다).
  collection_holding_added: ["cardSerialNo", "source", "hasPurchasePrice"],
  collection_holding_updated: ["cardSerialNo"],
  collection_holding_removed: ["cardSerialNo"],
};

/** 앱 코드에서 설정 가능한 user property 키 — platform/ota_version 은 analytics.ts 가 내부 설정. */
const USER_PROPERTY_ALLOWLIST: readonly string[] = [];

function devWarn(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn("[track]", ...args);
  }
}

/** allow-list 필터 — 등록되지 않은 키·null 값 제거. */
function filterProperties(event: EventName, properties: EventProperties): AnalyticsParams {
  const allowed = EVENT_PARAM_ALLOWLIST[event] ?? [];
  const filtered: AnalyticsParams = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === null) continue;
    if (!allowed.includes(key)) {
      devWarn(`"${event}" 파라미터 "${key}" 는 allow-list 에 없어 제거됨 — tracking.ts 참고`);
      continue;
    }
    filtered[key] = value;
  }
  return filtered;
}

export function track(event: EventName, properties: EventProperties = {}): void {
  if (env.VITE_ENABLE_LOGGING) {
    // eslint-disable-next-line no-console
    console.log("[track]", event, properties);
  }
  logEvent(event, filterProperties(event, properties));
}

export function setUserProperty(key: string, value: string | number | boolean | null): void {
  if (env.VITE_ENABLE_LOGGING) {
    // eslint-disable-next-line no-console
    console.log("[track:user]", key, value);
  }
  if (!USER_PROPERTY_ALLOWLIST.includes(key)) {
    devWarn(`user property "${key}" 는 allow-list 에 없어 제거됨 — tracking.ts 참고`);
    return;
  }
  setAnalyticsUserProperty(key, value === null ? null : String(value));
}

/** 로그인 성공 시 내부 회원 일련번호를 GA user_id 로 설정 (가명 식별자). */
export function identifyUser(userId: string | number): void {
  if (env.VITE_ENABLE_LOGGING) {
    // eslint-disable-next-line no-console
    console.log("[track:identify]", userId);
  }
  setAnalyticsUserId(String(userId));
}

/** 로그아웃 시 user_id 해제. */
export function resetUser(): void {
  if (env.VITE_ENABLE_LOGGING) {
    // eslint-disable-next-line no-console
    console.log("[track:reset]");
  }
  setAnalyticsUserId(null);
}
